import React, { useEffect, useRef, useState } from "react";
import { SwitchTransition, CSSTransition } from "react-transition-group";
import QuoteTableExpanded from "./QuoteTableExpanded";
import AircraftCardActions from "./AircraftCardActions";
import Floorplans from "./Floorplans";
import AircraftCardCollapsibleSection from "./AircraftCardCollapsibleSection";
import AircraftCardFeatures from "./AircraftCardFeatures";
import chevronUp from "../../assets/icons/chevron-up-big.svg";

const AircraftCardExtraContent = ({ aircraftData, sharedPage, legId, tripInfo, showMovida, onCollapse }) => {
    const [content, setContent] = useState();
    const [showMoreAmenities, setShowMoreAmenities] = useState(false);
    const [isAmenitiesExpanded, setIsAmenitiesExpanded] = useState(true);
    const [isScheduleExpanded, setIsScheduleExpanded] = useState(true);
    const [isNotesExpanded, setIsNotesExpanded] = useState(true);
    const [isFloorplansExpanded, setIsFloorplansExpanded] = useState(true);
    const swapFade = useRef();

    useEffect(() => {
        if (aircraftData && (!content || content.id !== aircraftData.id)) {
            setContent(aircraftData);
            setShowMoreAmenities(false);
            setIsAmenitiesExpanded(true);
            setIsScheduleExpanded(true);
            setIsNotesExpanded(true);
            setIsFloorplansExpanded(true);
        }
    }, [aircraftData, content]);

    if (!content) return null;

    const hasManyLegs = Array.isArray(tripInfo?.legs) && tripInfo.legs.length > 3;

    const bookingActions = (minimal) => (
        <AircraftCardActions
            price={content.price}
            operatorCurrency={content.operatorCurrency}
            currency={content.preferredCurrency || content.currency}
            isPortalPage={false}
            sharedPage={sharedPage}
            approvalRequested={false}
            thumbsStatus={false}
            updateThumbsStatus={() => {}}
            showMovida={showMovida}
            legId={legId}
            aircraftId={content.id}
            tailNumber={content.tailNumber}
            handlePortalBooking={null}
            onInfoClick={null}
            expanded={true}
            minimal={minimal}
        />
    );

    const featuresBlock = (
        <AircraftCardCollapsibleSection
            title="FEATURES"
            isExpanded={isAmenitiesExpanded}
            onToggle={() => setIsAmenitiesExpanded(!isAmenitiesExpanded)}
            contentClassName="aircraft-card-extra-content__column-content"
        >
            <AircraftCardFeatures
                content={content}
                showMoreAmenities={showMoreAmenities}
                onShowMoreToggle={() => setShowMoreAmenities(!showMoreAmenities)}
            />
        </AircraftCardCollapsibleSection>
    );
    const scheduleBlock = (
        <AircraftCardCollapsibleSection
            title="SCHEDULE"
            isExpanded={isScheduleExpanded}
            onToggle={() => setIsScheduleExpanded(!isScheduleExpanded)}
        >
            <QuoteTableExpanded
                departureTime={content.departureDateTime || content.departureTime}
                arrivalTime={content.arrivalDateTime}
                departureAirport={content.departureAirport}
                arrivalAirport={content.arrivalAirport}
                duration={content.duration}
                fuelStop={content.fuelStops || content.fuelStop}
                passengers={content.seats}
                luggage={tripInfo?.legs?.[0]?.luggage ?? content.luggage}
                legData={tripInfo?.legs}
                ownerApproval={content.ownerApproval}
            />
        </AircraftCardCollapsibleSection>
    );

    const notesBlock =
        content.notes && content.notes.trim().length > 0 ? (
            <AircraftCardCollapsibleSection
                title="NOTES"
                isExpanded={isNotesExpanded}
                onToggle={() => setIsNotesExpanded(!isNotesExpanded)}
            >
                <p className="aircraft-card-extra-content__column__small-print">{content.notes}</p>
            </AircraftCardCollapsibleSection>
        ) : null;

    const floorplansBlock = (
        <AircraftCardCollapsibleSection
            title="FLOORPLANS"
            isExpanded={isFloorplansExpanded}
            onToggle={() => setIsFloorplansExpanded(!isFloorplansExpanded)}
            showDesktopTitle={false}
        >
            <Floorplans
                floorPlan={content.floorPlan}
                cabinLength={content.cabinLength}
                headRoom={content.headRoom}
                cabinWidth={content.cabinWidth}
            />
        </AircraftCardCollapsibleSection>
    );

    return (
        <SwitchTransition mode="out-in">
            <CSSTransition
                key={content.id}
                timeout={500}
                unmountOnExit
                mountOnEnter
                nodeRef={swapFade}
                classNames="aircraft-card-extra-content-"
            >
                <div
                    ref={swapFade}
                    className={`aircraft-card-extra-content aircraft-card-extra-content--exit-complete aircraft-card-extra-content--layout${
                        hasManyLegs ? " aircraft-card-extra-content--multi-legs" : ""
                    }`}
                >
                    <div className="aircraft-card-extra-content__layout-schedule-row">{scheduleBlock}</div>
                    <div className="aircraft-card-extra-content__layout-features-floorplans-row">
                        <div className="aircraft-card-extra-content__layout-features-col">{featuresBlock}</div>
                        <div className="aircraft-card-extra-content__layout-floorplans-wrap">{floorplansBlock}</div>
                        <div className="aircraft-card-extra-content__layout-booking-mobile">
                            {bookingActions(false)}
                        </div>
                    </div>
                    {notesBlock && (
                        <div className="aircraft-card-extra-content__layout-notes-row">
                            <div className="aircraft-card-extra-content__column aircraft-card-extra-content__column--notes">
                                {notesBlock}
                            </div>
                        </div>
                    )}
                    {onCollapse && (
                        <button className="aircraft-card-extra-content__collapse-btn" onClick={onCollapse}>
                            <img src={chevronUp} alt="Collapse" />
                        </button>
                    )}
                </div>
            </CSSTransition>
        </SwitchTransition>
    );
};

export default AircraftCardExtraContent;
